/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MenuList, Typography2 } from '@coursera/cds-core';

import type { ContextProps } from 'bundles/enterprise-admin-home/components/ContextButton';
import { OrgContextAvatar } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/items/OrgContextAvatar';

const USER_HOME_HREF = '/?skipBrowseRedirect=true';

const styles = {
  link: css`
    &&,
    &&:link,
    &&:visited,
    &&:hover,
    &&:focus,
    &&:active {
      text-decoration: none;
      color: var(--cds-color-neutral-primary);
    }
  `,
  content: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
  `,
  avatar: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: var(--cds-border-radius-50);
    overflow: hidden;

    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  text: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  `,
  truncate: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
};

type Props = Pick<ContextProps, 'href' | 'header' | 'label' | 'photoUrl' | 'primaryColor' | 'iconColor'> & {
  /** Fires after the internal nav handler — used by the popup to close itself on activate. */
  onClick?: () => void;
};

export const OrgContextItem = ({ href, header, label, photoUrl, primaryColor, iconColor, onClick }: Props) => {
  const ariaLabel = label ? `${header}, ${label}` : header;
  const isUserHome = href === USER_HOME_HREF;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.();
  };

  return (
    <MenuList.ItemLink href={href} css={styles.link} aria-label={ariaLabel} onClick={handleClick}>
      <span css={styles.content}>
        <span css={styles.avatar} aria-hidden="true">
          <OrgContextAvatar
            photoUrl={photoUrl}
            isUserHome={isUserHome}
            label={label}
            primaryColor={primaryColor}
            iconColor={iconColor}
          />
        </span>
        <span css={styles.text}>
          <Typography2 variant="actionSecondary" component="span" color="body" css={styles.truncate}>
            {header}
          </Typography2>
          {label && (
            <Typography2 variant="bodyTertiary" component="span" color="supportText" css={styles.truncate}>
              {label}
            </Typography2>
          )}
        </span>
      </span>
    </MenuList.ItemLink>
  );
};

export default OrgContextItem;
